import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Mail, Lock, ArrowRight, Eye, EyeOff, Loader2, Fingerprint, Users, BarChart3, Zap } from 'lucide-react';
import api from '@/lib/api';
// The ceremony itself lives in AuthContext.loginWithStepUp, so every
// localStorage write stays in setSession. Only the capability probe and the
// error humaniser are needed here.
import { browserSupportsWebAuthn, describeWebAuthnError } from '@/lib/webauthn';
import { getSignupAttribution } from '@/lib/signupAttribution';

// Logo URLs
const EVA_LOGO_URL = "/eva-icon-square.png";
const VISTRY_LOGO_URL = "/vistry-icon.png";

// Microsoft icon SVG
// In-app path only, never an absolute URL -- this comes from a query string
// an attacker can set, so it must not be handed to navigate()/window.location
// unchecked. Mirrors SSOPage.js's getSafeRedirect.
function isSafeNextPath(path) {
  return typeof path === 'string' && path.startsWith('/') && !path.startsWith('//');
}

const MicrosoftIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 21 21">
    <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
    <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
    <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
  </svg>
);

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState(null);
  const [error, setError] = useState('');
  const [oauthStatus, setOauthStatus] = useState({ microsoft: { enabled: false } });
  const [oauthLoading, setOauthLoading] = useState(true);
  // Two-phase login (EIP-319). 'credentials' -> 'step-up'. The step-up token
  // lives here in component state only and is never persisted: it is a bearer
  // credential for completing a superadmin login, and localStorage is readable
  // by anything running on the origin.
  const [phase, setPhase] = useState('credentials');
  const [stepUpToken, setStepUpToken] = useState(null);
  // EIP-571: the WebAuthn challenge, fetched BEFORE the user clicks so the
  // ceremony can start inside the click's user activation. iOS Safari refuses
  // navigator.credentials.get() once that activation has been spent on a
  // network round trip; desktop Chrome does not, which is why this only
  // failed on mobile.
  const [stepUpChallenge, setStepUpChallenge] = useState(null);
  // A ref, not the state above: React.StrictMode double-invokes effects in
  // development, and every call to /passkey/step-up/options rebinds the
  // step-up to a NEW challenge id, orphaning the previous one. Verify then
  // rejects with "Challenge does not match this step-up."
  const stepUpFetchedForRef = useRef(null);
  const [stepUpLoading, setStepUpLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, fetchStepUpOptions, completeStepUp, setSession } = useAuth();

  // The old passkey login button, its /passkey/check debounce, and
  // handlePasskeyLogin were removed in EIP-319. That flow was PASSWORDLESS — a
  // valid assertion alone minted a full superadmin session, i.e. "either
  // factor" rather than two — and the check endpoint it depended on was a
  // public superadmin-enumeration oracle hit on every keystroke. Both endpoints
  // are gone; the step-up panel below replaces them.

  const resetToCredentials = () => {
    setPhase('credentials');
    setStepUpToken(null);
    setStepUpChallenge(null);
    stepUpFetchedForRef.current = null;
    setStepUpLoading(false);
    setPassword('');
  };

  // EIP-571: fetch the challenge as soon as we enter the step-up phase, not
  // when the button is clicked. Guarded by a ref so StrictMode's double-invoke
  // cannot rebind the step-up to a second challenge and orphan the first.
  useEffect(() => {
    if (phase !== 'step-up' || !stepUpToken) return;
    if (stepUpFetchedForRef.current === stepUpToken) return;
    stepUpFetchedForRef.current = stepUpToken;

    let cancelled = false;
    (async () => {
      try {
        const challenge = await fetchStepUpOptions(stepUpToken);
        if (!cancelled) setStepUpChallenge(challenge);
      } catch (err) {
        console.error('Failed to prepare the passkey challenge:', err);
        // Leave stepUpChallenge null; the click handler fetches on demand and
        // surfaces the real error there rather than blocking the panel now.
        if (!cancelled) stepUpFetchedForRef.current = null;
      }
    })();
    return () => { cancelled = true; };
  }, [phase, stepUpToken, fetchStepUpOptions]);

  const handleStepUp = async () => {
    if (!browserSupportsWebAuthn()) {
      setError(
        'This browser does not support passkeys. Sign in from a browser that does, ' +
        'or contact a platform administrator.'
      );
      return;
    }
    setError('');
    try {
      // Do NOT await a network call before getAssertion when a pre-fetched
      // challenge is available: that is the whole point of this change. The
      // fallback below only runs when the pre-fetch failed, in which case the
      // ceremony was never going to start anyway.
      const challenge = stepUpChallenge || await fetchStepUpOptions(stepUpToken);
      setStepUpLoading(true);
      const data = await completeStepUp(stepUpToken, challenge.options, challenge.challengeId);
      const next = isSafeNextPath(searchParams.get('next')) ? searchParams.get('next') : null;
      navigate(data?.user?.force_password_change ? '/settings/profile' : (next || '/'));
    } catch (err) {
      console.error('Passkey step-up failed:', err);
      // A step-up token is single-use and capped at 3 attempts, so a 401 here
      // means it is spent — send the user back to the password form rather than
      // leaving them retrying a token the server will never accept again.
      if (err.response?.status === 401) {
        setError('That sign-in attempt expired. Please enter your password again.');
        resetToCredentials();
      } else {
        // Re-fetch rather than reuse: a failed ceremony leaves the bound
        // challenge spent from the user's point of view, and options are
        // rebound on every call, so retrying with the stale one would fail
        // with a challenge mismatch rather than the real reason.
        setStepUpChallenge(null);
        stepUpFetchedForRef.current = null;
        setError(describeWebAuthnError(err));
        setStepUpLoading(false);
      }
    }
  };

  // Fetch OAuth status on mount
  useEffect(() => {
    const fetchOAuthStatus = async () => {
      try {
        const response = await api.get('/auth/oauth-status');
        setOauthStatus(response.data);
      } catch (err) {
        console.error('Failed to fetch OAuth status:', err);
        // Keep defaults (disabled)
      } finally {
        setOauthLoading(false);
      }
    };
    fetchOAuthStatus();
  }, []);

  // Handle Microsoft OAuth callback (code in URL params)
  const handleMicrosoftCallback = useCallback(async () => {
    const code = searchParams.get('code');
    if (code) {
      setSocialLoading('processing');
      try {
        const redirectUri = window.location.origin + '/login';
        const response = await api.post('/auth/microsoft/callback', {
          code,
          redirect_uri: redirectUri,
          attribution: getSignupAttribution(),
        });
        
        // setSession rather than writing localStorage directly: it also clears
        // the stale eva_viewing_as_superadmin key and updates context state, so
        // no full page reload is needed.
        setSession(response.data);
        window.history.replaceState(null, '', '/');
        // No explicit navigate here -- App.js's PublicRoute reacts to the
        // user state update above and is the SOLE reader of eva_login_next
        // (see handleMicrosoftLogin). A second reader here raced it: both
        // this handler and PublicRoute wanted to read-then-clear the same
        // sessionStorage key, and whichever ran first won a redirect target
        // the other had already thrown away.
      } catch (err) {
        console.error('Microsoft OAuth error:', err);
        setError(err.response?.data?.detail || 'Microsoft sign-in failed. Please try again.');
        window.history.replaceState(null, '', '/login');
        setSocialLoading(null);
      }
    }
  }, [searchParams, setSession]);

  useEffect(() => {
    handleMicrosoftCallback();
  }, [handleMicrosoftCallback]);

  // Google sign-in was removed in EIP-319 (closes EIP-314). It was never a
  // Google integration: the button redirected to auth.emergentagent.com and the
  // callback read the user's identity from demobackend.emergentagent.com --
  // third-party hosts we do not control -- then posted that email to a backend
  // endpoint that trusted it and derived superadmin status from it. Leftover
  // bootstrap scaffolding whose only working behaviour was an unauthenticated
  // superadmin backdoor. Microsoft SSO does a real MSAL code exchange and
  // remains the supported IdP.


  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const data = await login(email, password);

      // Discriminated response: a latched superadmin gets a step-up challenge
      // rather than a session. Clear `loading` explicitly — the success path
      // normally navigates away, so nothing else would re-enable the form.
      if (data?.status === 'step_up_required') {
        setStepUpToken(data.step_up_token);
        setPhase('step-up');
        setLoading(false);
        return;
      }

      const next = isSafeNextPath(searchParams.get('next')) ? searchParams.get('next') : null;
      navigate(data?.user?.force_password_change ? '/settings/profile' : (next || '/'));
    } catch (err) {
      // Get the most useful error message
      let errorMessage = 'Login failed. Please check your credentials.';
      
      if (err.isNetworkError || err.message?.includes('connect') || err.message === 'Network Error') {
        errorMessage = 'Unable to connect to the server. Please ensure the backend is running and try again.';
      } else if (err.message?.includes('JSON') || err.message?.includes('Unexpected')) {
        errorMessage = 'Server returned an invalid response. Please try again in a moment.';
      } else if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      setLoading(false);
    }
  };

  const handleMicrosoftLogin = async () => {
    setSocialLoading('microsoft');
    setError('');
    try {
      const redirectUri = window.location.origin + '/login';
      // redirect_uri must exactly match what's registered with the IdP, so
      // `next` can't ride along in it -- stash it and pick it back up in
      // handleMicrosoftCallback once Microsoft returns control to us.
      const next = isSafeNextPath(searchParams.get('next')) ? searchParams.get('next') : null;
      if (next) {
        sessionStorage.setItem('eva_login_next', next);
      } else {
        sessionStorage.removeItem('eva_login_next');
      }
      const response = await api.get(`/auth/microsoft/login?redirect_uri=${encodeURIComponent(redirectUri)}`);
      window.location.href = response.data.auth_url;
    } catch (err) {
      console.error('Microsoft login error:', err);
      let errorMessage = 'Microsoft sign-in failed. Please try again.';
      if (err.isNetworkError || err.message?.includes('connect')) {
        errorMessage = 'Unable to connect to the server. Please ensure the backend is running.';
      } else if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      }
      setError(errorMessage);
      setSocialLoading(null);
    }
  };

  // Show loading state during OAuth processing
  if (socialLoading === 'processing') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted">
        <Card className="w-full max-w-md">
          <CardContent className="pt-10 pb-10 text-center">
            <img src={EVA_LOGO_URL} alt="Eva" className="w-16 h-16 rounded-xl mx-auto mb-4" />
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary mb-4" />
            <h2 className="text-xl font-semibold text-foreground">Signing you in...</h2>
            <p className="text-muted-foreground mt-2">Please wait while we complete your sign-in.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 p-12 flex-col justify-between relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900" />
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMiIvPjwvZz48L2c+PC9zdmc+')] opacity-50" />
        
        <div className="relative z-10">
          <div className="flex items-center gap-3 text-white">
            <img src={EVA_LOGO_URL} alt="Eva" className="w-12 h-12 rounded-xl shadow-lg" />
            <span className="text-2xl font-bold">Eva Insights</span>
          </div>
        </div>
        
        <div className="space-y-8 relative z-10">
          <h1 className="text-4xl font-bold text-white leading-tight">
            Transform Your<br />HR Operations
          </h1>
          <p className="text-emerald-100/80 text-lg leading-relaxed max-w-md">
            See exactly how Eva is empowering your workforce. Track adoption, identify knowledge gaps, and prove ROI to leadership.
          </p>
          
          <div className="space-y-4">
            <div className="flex items-center gap-3 text-emerald-100/90">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                <Users className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="font-medium text-white">Reduce HR Inquiries by 70%</p>
                <p className="text-sm text-emerald-100/60">Employees find answers instantly</p>
              </div>
            </div>
            <div className="flex items-center gap-3 text-emerald-100/90">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="font-medium text-white">Data-Driven HR Decisions</p>
                <p className="text-sm text-emerald-100/60">Real-time analytics and trends</p>
              </div>
            </div>
            <div className="flex items-center gap-3 text-emerald-100/90">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                <Zap className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="font-medium text-white">Always-On HR Support</p>
                <p className="text-sm text-emerald-100/60">24/7 consistent policy guidance</p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="text-emerald-100/50 text-sm relative z-10 flex items-center gap-2">
          <img src={VISTRY_LOGO_URL} alt="Vistry" className="w-5 h-5 rounded" />
          Powered by vistry.ai
        </div>
      </div>
      
      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-secondary">
        <div className="w-full max-w-md">
          <Card className="border-border shadow-xl bg-card/80 backdrop-blur">
            <CardHeader className="space-y-1 pb-6">
              <div className="lg:hidden flex items-center gap-2 mb-4">
                <img src={EVA_LOGO_URL} alt="Eva" className="w-10 h-10 rounded-xl" />
                <span className="text-xl font-bold eva-gradient-text">Eva Insights</span>
              </div>
              <CardTitle className="text-2xl font-bold text-foreground">
                Welcome back
              </CardTitle>
              <CardDescription className="text-muted-foreground">
                Sign in to your account
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              {searchParams.get('expired') && !error && (
                <div className="text-sm text-warning bg-warning/10 p-3 rounded-xl border border-warning/20">
                  Your session expired. Please sign in again.
                </div>
              )}
              {error && (
                <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-xl border border-destructive/20">
                  {error}
                </div>
              )}

              {/* Social Login Buttons - Hidden until OAuth is configured
                  To re-enable: uncomment the block below and remove this comment wrapper
              {!oauthLoading && oauthStatus.microsoft.enabled && (
                <>
                  <div className="grid grid-cols-1 gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      disabled={!oauthStatus.microsoft.enabled || socialLoading}
                      onClick={handleMicrosoftLogin}
                      className={`h-11 ${!oauthStatus.microsoft.enabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                      data-testid="microsoft-login-btn"
                      title={oauthStatus.microsoft.enabled ? 'Sign in with Microsoft' : 'Microsoft sign-in not configured'}
                    >
                      {socialLoading === 'microsoft' ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <MicrosoftIcon />
                      )}
                      <span className="ml-2">Microsoft</span>
                    </Button>
                  </div>

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-card px-2 text-muted-foreground">
                        Or continue with email
                      </span>
                    </div>
                  </div>
                </>
              )}
              */}

              {phase === 'step-up' ? (
                /* Step-up REPLACES the password form rather than sitting beside
                   it: the password has already been accepted, and leaving it on
                   screen invites the user to retype it into a form whose submit
                   would start a second login and discard this challenge. */
                <div className="space-y-5" data-testid="step-up-panel">
                  <div className="text-center space-y-2">
                    <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <Fingerprint className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="text-lg font-semibold text-foreground">
                      Confirm it&apos;s you
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Your password was accepted. This account requires a passkey to finish signing in.
                    </p>
                  </div>

                  <Button
                    type="button"
                    className="w-full h-11 bg-gradient-to-r from-slate-800 to-emerald-700 hover:from-slate-700 hover:to-emerald-600 text-white font-medium transition-all"
                    onClick={handleStepUp}
                    disabled={stepUpLoading}
                    data-testid="step-up-button"
                  >
                    {stepUpLoading ? (
                      <span className="flex items-center gap-2">
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Waiting for your passkey...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <Fingerprint className="w-5 h-5" />
                        Continue with passkey
                      </span>
                    )}
                  </Button>

                  <button
                    type="button"
                    onClick={() => {
                      resetToCredentials();
                      setError('');
                    }}
                    className="w-full text-center text-sm text-muted-foreground hover:text-foreground"
                    data-testid="step-up-cancel"
                  >
                    Use a different account
                  </button>
                </div>
              ) : (
              <>
              {/* Email/Password Form */}
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      type="email"
                      placeholder="you@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 h-11 border-border focus:border-primary focus:ring-primary"
                      data-testid="email-input"
                      required
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-foreground">Password</label>
                    <Link 
                      to="/forgot-password" 
                      className="text-sm text-primary hover:underline"
                    >
                      Forgot password?
                    </Link>
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 pr-10 h-11 border-border focus:border-primary focus:ring-primary"
                      data-testid="password-input"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-muted-foreground"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                
                <Button
                  type="submit"
                  className="w-full h-11 bg-gradient-to-r from-slate-800 to-emerald-700 hover:from-slate-700 hover:to-emerald-600 text-white font-medium transition-all"
                  disabled={loading || socialLoading}
                  data-testid="login-button"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Signing in...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      Sign in
                      <ArrowRight className="w-5 h-5" />
                    </span>
                  )}
                </Button>
              </form>
              </>
              )}
              
              <p className="text-center text-sm text-muted-foreground dark:text-muted-foreground pt-2">
                Don&apos;t have an account?{' '}
                <Link to="/signup" className="text-primary hover:underline font-medium">
                  Sign up
                </Link>
              </p>
              
              <p className="text-center text-xs text-muted-foreground mt-2">
                By signing in, you agree to our{' '}
                <a href="https://vistry.ai/tos" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  Terms of Service
                </a>
                {' '}and{' '}
                <a href="https://vistry.ai/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  Privacy Policy
                </a>
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
